import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Send } from 'lucide-react';
import type { BackofficePermissionCode } from '@kardume/types';
import { PageHeader, SegmentedControl } from '@kardume/ui';
import { hasAnyPermission } from '../utils/access';
import CampaignsTab from '../components/messaging/CampaignsTab';
import ContactListsTab from '../components/messaging/ContactListsTab';
import ContactsTab from '../components/messaging/ContactsTab';
import TemplatesTab from '../components/messaging/TemplatesTab';
import InstancesTab from '../components/messaging/InstancesTab';
import OptOutsTab from '../components/messaging/OptOutsTab';

const TabBar = styled.div`
  margin-bottom: 1.25rem;
`;

type MessagingTab = 'campaigns' | 'lists' | 'contacts' | 'templates' | 'instances' | 'opt-outs';

const TAB_OPTIONS: { value: MessagingTab; label: string }[] = [
  { value: 'campaigns', label: 'Campanhas' },
  { value: 'lists', label: 'Listas' },
  { value: 'contacts', label: 'Contatos' },
  { value: 'templates', label: 'Templates' },
  { value: 'instances', label: 'Instâncias' },
  { value: 'opt-outs', label: 'Opt-out' },
];

interface MessagingPageProps {
  permissions: string[];
  isAdmin: boolean;
}

/**
 * Disparos de WhatsApp: campanhas, listas de prospecção, templates, os números
 * usados e quem pediu para não ser mais contatado.
 *
 * Duas capacidades são separadas na tela porque são riscos diferentes: `canManage`
 * monta (lista, template, campanha em rascunho) e `canSend` aperta o botão. Errar a
 * lista se corrige; mensagem enviada não volta.
 */
const MessagingPage: React.FC<MessagingPageProps> = ({ permissions, isAdmin }) => {
  const [tab, setTab] = useState<MessagingTab>('campaigns');

  const canManage = useMemo(
    () => hasAnyPermission(permissions, ['ROLE_BACKOFFICE_MESSAGING_MANAGE'] as BackofficePermissionCode[], isAdmin),
    [permissions, isAdmin]
  );
  const canSend = useMemo(
    () => hasAnyPermission(permissions, ['ROLE_BACKOFFICE_MESSAGING_SEND'] as BackofficePermissionCode[], isAdmin),
    [permissions, isAdmin]
  );

  return (
    <>
      <PageHeader
        title="Disparos"
        subtitle="Mensagens de WhatsApp para lojistas e usuários, enviadas em ritmo controlado para não bloquear o número."
        actions={<Send size={18} />}
      />

      <TabBar>
        <SegmentedControl options={TAB_OPTIONS} value={tab} onChange={setTab} />
      </TabBar>

      {tab === 'campaigns' && <CampaignsTab canManage={canManage} canSend={canSend} />}
      {tab === 'lists' && <ContactListsTab canManage={canManage} />}
      {tab === 'contacts' && <ContactsTab canManage={canManage} />}
      {tab === 'templates' && <TemplatesTab canManage={canManage} />}
      {tab === 'instances' && <InstancesTab canManage={canManage} canSend={canSend} />}
      {tab === 'opt-outs' && <OptOutsTab canManage={canManage} />}
    </>
  );
};

export default MessagingPage;
